import { useTranslation } from 'react-i18next';

import { CountrySelectionList } from '../../../../components/CountrySelectionList';
import type { Country } from '../../../../domain/country';
import { useUserQuery } from '../../../../features/user/useUserQuery';
import { CountryService, WORLDWIDE_COUNTRY_ID } from '../../../../services/country/country';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

import {
  StyledCountriesContainer,
  StyledRadioButton,
  StyledCountrySelectionList,
  StyledCountriesListLabel,
} from './styles';

export type CountriesSelectStepProps = {
  countries: number[];
  onChange(countries: number[]): void;
};

export const CountriesSelectStep = ({ countries, onChange }: CountriesSelectStepProps) => {
  const dispatchTrackEvent = useDispatchTrackEventWithLocation();
  const { t } = useTranslation();
  const availableCountries = useUserQuery().data?.availableCountries ?? [];
  const isWorldwideSelected = CountryService.isWorldwideIncluded(countries);
  const isWorldwideAvailable = CountryService.isWorldwideIncluded(availableCountries);
  const handleSelectWorldwide = () => {
    dispatchTrackEvent('preferences', 'select country', 'worldwide/1');
    onChange([WORLDWIDE_COUNTRY_ID]);
  };

  const handleSelectSpecificCountries = () => {
    onChange([]);
  };

  const handleClickOnCountry = (country: Country) => {
    const isCountrySelected = countries.includes(country.id);
    const newCountries = isCountrySelected ? countries.filter((c) => c !== country.id) : [...countries, country.id];
    const action = isCountrySelected ? 'remove country' : 'select country';
    dispatchTrackEvent('preferences', action, `${country.text}/${newCountries.length}`);
    onChange(newCountries);
  };

  const totalCountries = isWorldwideAvailable ? availableCountries.length - 1 : availableCountries.length;
  const selectedOutOfTotal = `(${countries.length}/${totalCountries})`;

  const label = isWorldwideSelected ? (
    t('insights.onboarding.countries.select_specific')
  ) : (
    <>
      <span>{t('insights.onboarding.countries.select_specific')}</span>
      {selectedOutOfTotal}
    </>
  );

  return (
    <StyledCountriesContainer>
      {isWorldwideAvailable ? (
        <>
          <StyledRadioButton
            label={t('common.worldwide')}
            selected={isWorldwideSelected}
            onClick={handleSelectWorldwide}
          />
          <StyledRadioButton label={label} selected={!isWorldwideSelected} onClick={handleSelectSpecificCountries} />
        </>
      ) : (
        <StyledCountriesListLabel>
          <span>{t('insights.onboarding.countries.selected')}</span>
          {selectedOutOfTotal}
        </StyledCountriesListLabel>
      )}
      <StyledCountrySelectionList isVisible={!isWorldwideSelected}>
        <CountrySelectionList
          initiallySelectedCountries={[]}
          initiallyNotSelectedCountries={CountryService.getCountriesByIds(availableCountries)}
          selectedCountryIdsSet={new Set(countries)}
          handleClickOnCountry={handleClickOnCountry}
          listMaxHeight={176}
        />
      </StyledCountrySelectionList>
    </StyledCountriesContainer>
  );
};
